import { IAccreditationApplication } from '../../../interfaces/IAccreditationApplication';
import { useAccreditationPayments } from '../../../hooks/useAccreditationPayments';
import { useRef, useState } from 'react';
import { BillingRequest } from '@gocardless/react-dropin';

const useAccreditationPaymentModal = (application: IAccreditationApplication) => {
    const { operations, models } = useAccreditationPayments();

    const [show, setShow] = useState(false);
    const [flowId, setFlowId] = useState<string | null>(null);
    const [envId, setEnvId] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const setupStartedRef = useRef(false);

    const handleShow = () => {
        setShow(true);
        return handleSetupPayment();
    };
    const handleClose = () => {
        setShow(false);
        setFlowId(null);
        setEnvId('');
    };

    const handleSetupPayment = async () => {
        if (setupStartedRef.current || flowId) return;

        setLoading(true);
        try {
            setupStartedRef.current = true;
            const payment = await operations.initialiseApplicationPayment(application);
            if (payment) {
                setFlowId(payment.flow_id!);
                setEnvId(payment.environment ?? '');
            } else {
                setFlowId(null);
                setEnvId('');
            }
        } catch (error) {
            console.error('Error setting up payment:', error);
        } finally {
            setLoading(false);
            setupStartedRef.current = false;
        }
    };

    const handleExit = (error: any, metadata: any) => {
        console.error('error', error);
        console.error('metadata', metadata);
        setLoading(false);
    };

    const handleGoCardlessPaymentSuccess = async (billingRequest: BillingRequest) => {
        setLoading(true);
        return operations
            .finaliseApplicationPayment(application, JSON.stringify(billingRequest), {
                created: billingRequest.created_at,
                id: billingRequest.id,
                status: billingRequest.status,
            })
            .then((_r) => {
                setFlowId(null);
                setEnvId('');
                setShow(false);
                return this;
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handlePayPalPaymentSuccess = async (responseData: any) => {
        setLoading(true);
        return operations
            .finaliseApplicationPayment(application, JSON.stringify(responseData), {
                created: responseData.purchase_units[0].payments.captures[0].create_time,
                id: responseData.id,
                status: responseData.status,
            })
            .then((_r) => {
                setFlowId(null);
                setEnvId('');
                setShow(false);
                return this;
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return {
        models: { show, envId, flowId, loading, error: models?.error || false },
        operations: {
            handleShow,
            handleClose,
            handleSetupPayment,
            handleGoCardlessPaymentSuccess,
            handlePayPalPaymentSuccess,
            handleExit,
        },
    };
};

export default useAccreditationPaymentModal;
